import React, { FC, createContext, useMemo, ReactNode } from "react";
import { useMutation, ApolloError } from "@apollo/client";
import { ManagedBy } from "@app/provider/testData/types";
import SimpleOrderService, {
  MarkOrderAsAccessedByPatientResponse,
} from "@app/service/simpleOrder";
import {
  PartnerInfoStepResolve,
  PatientAcknowledgmentStepResolve,
} from "./simpleOrder/SimpleOrderStepperProvider";
import { ShippingAddress } from "./types";

export enum SimpleOrderStage {
  START = "START",
  PERSONAL_INFO = "PERSONAL_INFO",
  KIT_SHIPPING_DETAILS = "KIT_SHIPPING_DETAILS",
  PARTNER_INFO = "PARTNER_INFO",
  BABY_GENDER = "BABY_GENDER",
  PAYMENT_METHOD = "PAYMENT_METHOD",
  SAMPLE_DRAW_PREFERENCE = "SAMPLE_DRAW_PREFERENCE",
  CONFIRMATION = "CONFIRMATION",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
}

export enum SimpleOrderPaymentMethod {
  COMMERCIAL = "COMMERCIAL",
  MEDICAID_GOVERNMENT = "MEDICAID_GOVERNMENT",
  SEVERAL_INSURANCES = "SEVERAL_INSURANCES",
  NO_PAYMENT = "NO_PAYMENT",
  SELF_PAID = "SELF_PAID",
}

export interface SimpleOrder {
  completionStage: SimpleOrderStage;
  watchedVideoPercentage: number;
  patient: {
    firstName: string;
    middleName?: string;
    lastName: string;
  };
  shippingAddress: {
    countryCode: string;
    city: string;
    state: string;
    street: string;
    zipCode: string;
  };
  partnerInfo: PartnerInfoStepResolve;
  reportGender: boolean;
  caPnsSupplemental: boolean;
  billingInfo: {
    paymentMethod: SimpleOrderPaymentMethod;
    primaryInsurance: {
      insuranceCompany: string;
      healthPlan: string;
      medicare: boolean;
      memberId: string;
      groupId: string;
      state: string;
    };
    secondaryInsurance?: {
      insuranceCompany: string;
      healthPlan: string;
      medicare: boolean;
      memberId: string;
      groupId: string;
      state: string;
    };
  };
  sampleManagedBy: ManagedBy;
  patientAcknowledgment: PatientAcknowledgmentStepResolve;
}

export enum InsuranceTypeDto {
  SINGLE = "SINGLE",
  MULTIPLE = "MULTIPLE",
}

type PaymentMethodInfoDto = {
  companyName: string;
  insuranceId: string;
};

type InsuranceInfoDto = {
  paymentMethod: string;
  paymentMethodInfo?: PaymentMethodInfoDto;
};

export type PaymentInfoDto = {
  insuranceType: InsuranceTypeDto;
  first: InsuranceInfoDto;
  second?: InsuranceInfoDto;
};

export interface SimpleOrderCompleteDto {
  shippingAddress: ShippingAddress;
  reportGender?: boolean;
  partnerInfo?: PartnerInfoStepResolve;
  payment: PaymentInfoDto;
  patientAcknowledgment: PatientAcknowledgmentStepResolve;
}

interface SimpleOrderProviderProps {
  orderUidPublic: string;
  children: ReactNode;
}

interface CancelSimpleOrderResponse {
  success: boolean;
}

export interface SimpleOrderController {
  completeSimpleOrder: (
    workflowUid: string,
    simpleOrder: SimpleOrderCompleteDto,
    testName?: string,
    limsId?: string
  ) => Promise<void>;
  completeSimpleOrderLoading: boolean;
  failedCompleteAttempts: number;
  setFailedCompleteAttempts: React.Dispatch<React.SetStateAction<number>>;
  cancelSimpleOrder: (testName?: string, limsId?: string) => Promise<void>;
  cancelSimpleOrderData?: CancelSimpleOrderResponse;
  cancelSimpleOrderError?: ApolloError;
  cancelSimpleOrderIsLoading?: boolean;
  markOrderAsAccessedByPatient: (
    orderUidPublic: string,
    workflowUid: string,
    testName?: string,
    limsClinicId?: string
  ) => Promise<void>;
}

export const Context = createContext<SimpleOrderController>({
  completeSimpleOrder: () => Promise.reject(),
  completeSimpleOrderLoading: false,
  failedCompleteAttempts: 0,
  setFailedCompleteAttempts: () => undefined,
  cancelSimpleOrder: () => Promise.reject(),
  cancelSimpleOrderIsLoading: false,
  markOrderAsAccessedByPatient: () => Promise.reject(),
});

Context.displayName = "SimpleOrderContext";

const SimpleOrderProvider: FC<SimpleOrderProviderProps> = ({
  orderUidPublic,
  children,
}) => {
  const [failedCompleteAttempts, setFailedCompleteAttempts] = React.useState(0);

  const [completeSimpleOrderQuery, { loading: completeSimpleOrderLoading }] =
    useMutation<{
      simpleOrder: SimpleOrder;
    }>(SimpleOrderService.completeSimpleOrder());

  const [markOrderAsAccessedByPatientQuery] = useMutation<{
    markOrderAsAccessedByPatient: MarkOrderAsAccessedByPatientResponse;
  }>(SimpleOrderService.markOrderAsAccessedByPatient());

  const completeSimpleOrder = async (
    workflowUid: string,
    simpleOrderInfo: SimpleOrderCompleteDto,
    testName: string,
    limsClinicId: string
  ) => {
    await completeSimpleOrderQuery({
      variables: {
        orderUidPublic,
        workflowUid,
        simpleOrderInfo,
        testName,
        limsClinicId,
      },
    });
  };

  const [
    cancelSimpleOrder,
    {
      error: cancelSimpleOrderError,
      data: cancelSimpleOrderData,
      loading: cancelSimpleOrderIsLoading,
    },
  ] = useMutation<{
    cancelSimpleOrder: CancelSimpleOrderResponse;
  }>(SimpleOrderService.cancelSimpleOrder());

  const handleCancelSimpleOrder = async (
    testName: string,
    limsClinicId: string
  ) => {
    await cancelSimpleOrder({
      variables: { orderUidPublic, testName, limsClinicId },
    });
  };

  const markOrderAsAccessedByPatient = async (
    orderUidPublic: string,
    workflowUid: string,
    testName: string,
    limsClinicId: string
  ) => {
    await markOrderAsAccessedByPatientQuery({
      variables: {
        orderUidPublic,
        workflowUid,
        testName,
        limsClinicId,
      },
    });
  };

  const simpleOrderController: SimpleOrderController = useMemo(
    () => ({
      completeSimpleOrder,
      completeSimpleOrderLoading,
      failedCompleteAttempts,
      setFailedCompleteAttempts,
      cancelSimpleOrder: handleCancelSimpleOrder,
      cancelSimpleOrderData: cancelSimpleOrderData?.cancelSimpleOrder,
      cancelSimpleOrderError,
      cancelSimpleOrderIsLoading,
      markOrderAsAccessedByPatient,
    }),
    [
      completeSimpleOrder,
      completeSimpleOrderLoading,
      failedCompleteAttempts,
      setFailedCompleteAttempts,
      handleCancelSimpleOrder,
      cancelSimpleOrderData,
      cancelSimpleOrderError,
      cancelSimpleOrderIsLoading,
      markOrderAsAccessedByPatient,
    ]
  );

  return (
    <Context.Provider value={simpleOrderController}>
      {children}
    </Context.Provider>
  );
};

export default SimpleOrderProvider;
